import { computed, inject, Injectable, signal } from '@angular/core';
import { ToolsService } from 'src/app/services/tools.service';
import { INFORMER_RESOURCES_STORAGE_KEY } from 'src/app/interfaces';

/** Tipos de contenido que el informador puede añadir al correo del visitante. */
export type InformerResourceType = 'point' | 'event' | 'hiking' | 'route';

/**
 * Recurso añadido al correo. Al back solo se envía lo que lo identifica —`id` y
 * `type`, y el token en las rutas—: el nombre, la imagen y el enlace los resuelve
 * él en el idioma del envío (así no viajan URLs del cliente a un correo firmado
 * por el destino). El resto de campos son para pintar el carrito.
 */
export interface InformerResource {
  id: number;
  type: InformerResourceType;
  /**
   * Solo en las rutas predefinidas: es lo que las identifica de verdad. Su `id` es
   * de la plantilla del asistente y al CMS no le dice nada; con el token se
   * instancia la ruta (que es lo que se envía, para que el visitante pueda
   * modificarla sin tocar la original) y se compone el enlace del correo.
   */
  token?: string | null;
  name: Record<string, string> | null;
  nameSlug?: Record<string, string> | null;
  image: string | null;
  /** Respaldos de imagen, para que la tarjeta caiga igual que en la home. */
  imageFallback?: string | null;
  categoryImage?: string | null;
  category_image?: string | null;
  duration?: number | null;
}

/**
 * HRS-2990: recursos que el informador va añadiendo para el correo del visitante
 * en el modo `complete`.
 *
 * Deliberadamente separado de `TechItemsRouteService`: ese alimenta la ruta que
 * calcula el asistente (y exige `is_schedulable`), mientras que aquí vale
 * cualquier contenido, porque solo se enlaza en un email.
 */
@Injectable({ providedIn: 'root' })
export class InformerResourcesService {
  private _tools = inject(ToolsService);

  /**
   * Se persiste con el mismo TTL que el modo del informador: el técnico añade
   * recursos mientras navega y el registro puede recargar la página (el envío
   * termina con un `location.reload()`), así que en memoria se perderían.
   */
  private _resources = signal<InformerResource[]>(this._restore());
  public resources = this._resources.asReadonly();

  public count = computed(() => this._resources().length);
  public isEmpty = computed(() => this._resources().length === 0);

  /** Lo que viaja en `post-register`. El token solo lo llevan las rutas. */
  public payload = computed(() => this._resources().map(({ id, type, token }) => (
    token ? { id, type, token } : { id, type }
  )));

  public has(id: number, type: InformerResourceType): boolean {
    return this._resources().some(resource => resource.id === id && resource.type === type);
  }

  /** Añade o quita: el `+` de las tarjetas es un interruptor. */
  public toggle(resource: InformerResource): void {
    this.has(resource.id, resource.type)
      ? this.remove(resource.id, resource.type)
      : this._set([...this._resources(), resource]);
  }

  public remove(id: number, type: InformerResourceType): void {
    this._set(this._resources().filter(resource => !(resource.id === id && resource.type === type)));
  }

  /**
   * Se vacía tras enviar el registro y al salir del modo: si no, el visitante
   * siguiente heredaría los recursos del anterior.
   */
  public clear(): void {
    this._set([]);
  }

  private _set(resources: InformerResource[]): void {
    this._resources.set(resources);

    if (resources.length === 0) {
      localStorage.removeItem(INFORMER_RESOURCES_STORAGE_KEY);
      return;
    }

    this._tools.setWithExpiry(INFORMER_RESOURCES_STORAGE_KEY, resources);
  }

  private _restore(): InformerResource[] {
    const stored = this._tools.getWithExpiry(INFORMER_RESOURCES_STORAGE_KEY);

    return Array.isArray(stored) ? stored : [];
  }
}

/** Tipos de contenido que aceptan el `+` del correo (los PDFs van por su propio selector). */
const RESOURCE_TYPES: InformerResourceType[] = ['point', 'event', 'hiking', 'route'];

/**
 * Traduce un item de tarjeta/detalle a recurso del correo. Devuelve `null` si el
 * tipo no es enlazable (pdf, categorías, ...) o si no hay id.
 */
export function toInformerResource(item: any): InformerResource | null {
  const type = item?.type as InformerResourceType;
  const id = Number(item?.id);

  if (!RESOURCE_TYPES.includes(type)) return null;

  const token = typeof item?.token === 'string' && item.token.trim() !== '' ? item.token : null;

  // A una ruta la identifica su token, no el id (que es de la plantilla del
  // asistente); al resto de contenidos, su id en el CMS.
  if (type === 'route') {
    if (!token) return null;
  }
  else if (!Number.isInteger(id) || id <= 0) {
    return null;
  }

  return {
    id,
    type,
    token,
    name: plainName(item?.name),
    nameSlug: item?.nameSlug ?? null,
    // Se guarda la cadena entera de respaldos: la tarjeta del carrito es la misma
    // de la home y resuelve la imagen igual que allí.
    image: item?.image ?? item?.img ?? null,
    imageFallback: item?.imageFallback ?? null,
    categoryImage: item?.categoryImage ?? null,
    category_image: item?.category_image ?? null,
    duration: item?.duration ?? null,
  };
}

/**
 * Los contenidos traen `{ es: 'Nombre' }`, pero las rutas predefinidas usan el
 * mapa del asistente, `{ es: { value, custom } }`. Se aplana al guardarlas para
 * que el carrito y las tarjetas pinten el nombre igual en los dos casos.
 */
function plainName(name: any): Record<string, string> | null {
  if (!name || typeof name !== 'object') return null;

  const plain: Record<string, string> = {};
  Object.entries(name).forEach(([lang, value]) => {
    const text = typeof value === 'string' ? value : (value as any)?.value;
    if (typeof text === 'string' && text !== '') plain[lang] = text;
  });

  return Object.keys(plain).length > 0 ? plain : null;
}
